<template>
  <header
    class="app-header"
    :class="{ 'is-scrolled': isScrolled, 'is-mobile-open': mobileOpen, 'is-solid': isSolid }"
  >
    <div class="header-inner">
      <a class="logo" href="/" @click.prevent="navigateHome">
        <img :src="logo" alt="ReMaps" />
      </a>
      <nav class="nav-links" :class="{ 'nav-open': mobileOpen }">
        <router-link
          v-for="item in navItems"
          :key="item.path"
          :to="item.path"
          class="nav-item"
          exact-active-class="nav-active"
          @click="closeMobile"
        >
          {{ item.label }}
        </router-link>
      </nav>
      <div class="header-actions"></div>
      <button class="hamburger" @click="toggleMobile">
        <span />
        <span />
        <span />
      </button>
    </div>
  </header>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import logo from '@/assets/images/home-logo.png'

const router = useRouter()
const route = useRoute()
const isScrolled = ref(false)
const mobileOpen = ref(false)

const navItems = [
  { label: '首页', path: '/' },
  { label: '寻找岗位', path: '/find-talent' },
  { label: '我们的服务', path: '/our-services' },
  { label: '行业动态', path: '/industry-trends' },
  { label: '关于我们', path: '/about-us' },
]

const isSolid = computed(() => route.meta.solidHeader === true)

function navigateHome() {
  router.push('/')
}

function toggleMobile() {
  mobileOpen.value = !mobileOpen.value
}

function closeMobile() {
  mobileOpen.value = false
}

function onScroll() {
  isScrolled.value = window.scrollY > 80
}

onMounted(() => {
  window.addEventListener('scroll', onScroll, { passive: true })
})

onUnmounted(() => {
  window.removeEventListener('scroll', onScroll)
})
</script>

<style lang="scss" scoped>
.app-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: 88px;
  padding-top: 42px;
  display: flex;
  align-items: center;
  transition: height 0.35s cubic-bezier(0.4, 0, 0.2, 1),
    padding-top 0.35s cubic-bezier(0.4, 0, 0.2, 1),
    background-color 0.35s cubic-bezier(0.4, 0, 0.2, 1);

  &.is-scrolled,
  &.is-solid {
    height: 56px;
    padding-top: 0;
    background-color: rgba(25, 44, 70, 1);
  }
}

.header-inner {
  display: flex;
  align-items: center;
  width: 100%;
  max-width: var(--content-width);
  margin: 0 auto;
  padding: 0 var(--content-px);
  height: 46px;
}

.logo {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  z-index: 1001;

  img {
    width: 82px;
    height: 22px;
    display: block;
  }
}

.nav-links {
  display: flex;
  align-items: center;
  gap: 50px;
  margin-left: 60px;
}

.nav-item {
  color: #fff;
  font-size: 16px;
  font-weight: 500;
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    opacity: 0.8;
  }
}

.nav-active {
  color: #25c4ff;
  font-weight: 600;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 28px;
  margin-left: auto;
}

.lang-switcher {
  display: flex;
  align-items: center;
  gap: 6px;
}

.lang-btn {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.65);
  font-size: 14px;
  cursor: pointer;
  padding: 2px 4px;
  transition: color 0.2s ease;

  &:hover {
    color: #fff;
  }

  &--active {
    color: #25c4ff;
    font-weight: 600;
  }
}

.lang-divider {
  color: rgba(255, 255, 255, 0.25);
  font-size: 14px;
  user-select: none;
}

.action-btn {
  height: 32px;
  padding: 0 16px;
  border-radius: 16px;
  font-size: 14px;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;
}

.employer-btn {
  background: #22c3ff;
  color: #071033;
  border: none;

  &:hover {
    opacity: 0.9;
  }
}

.candidate-btn {
  background: transparent;
  color: #fff;
  border: 1px solid #fff;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
}

.hamburger {
  display: none;
  flex-direction: column;
  gap: 5px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  z-index: 1001;
  margin-left: auto;

  span {
    display: block;
    width: 24px;
    height: 2px;
    background: #fff;
    border-radius: 1px;
    transition: all 0.3s ease;
  }
}

@media (max-width: 1023px) {
  .nav-links {
    gap: 20px;
    margin-left: 32px;
  }
}

@media (max-width: 767px) {
  .app-header {
    height: 56px;
    padding-top: 0;
    background-color: rgba(25, 44, 70, 0.85);
  }

  .hamburger {
    display: flex;
  }

  .nav-links {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(25, 44, 70, 0.98);
    flex-direction: column;
    justify-content: center;
    gap: 32px;
    margin-left: 0;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.3s ease;

    &.nav-open {
      opacity: 1;
      pointer-events: auto;
    }
  }

  .nav-item {
    font-size: 24px;
  }

  .header-actions {
    position: fixed;
    top: 16px;
    right: 60px;
    z-index: 1002;
    gap: 12px;
  }

  .lang-switcher {
    gap: 4px;
  }

  .lang-btn {
    font-size: 13px;
  }
}
</style>
